import { policyType, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyDetailsFromSegments } from '@ourbranch/policy-utils';

import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import { searchForNewerStaffEditedOffer } from '../search-store/search.service';
import { GET_MOST_RECENT_STAFF_EDITED_OFFER, GET_POLICY_FROM_OFFER_ID } from './offer-queries';

/**
 * Umbrella eligibility inputs sourced from the linked (existing) customer's in-force
 * policies, for bundle-to-existing offers (HB/AB) whose own form has no autoCoverage (HB)
 * or homeCoverage (AB) to validate against. Returns null when the linked policies haven't
 * loaded, so callers fail closed.
 */
export const getLinkedPolicyUmbrellaInputs = (existingCustomerPolicies) => {
  if (!existingCustomerPolicies?.length) {
    return null;
  }
  const currentPolicyDetails = (wantedType) => {
    const candidates = existingCustomerPolicies.filter(
      (p) =>
        p.policyType === wantedType && ![PolicyStatus.Cancelled, PolicyStatus.Inactive].includes(getPolicyStatus(p))
    );
    if (!candidates.length) {
      return null;
    }
    // More than one non-cancelled policy (e.g. active term + renewal): use the oldest,
    // matching setOfferMarketTier
    return policyDetailsFromSegments(candidates.reduce((a, b) => (a.effectiveDate < b.effectiveDate ? a : b)));
  };
  const auto = currentPolicyDetails(policyType.Auto);
  const home = currentPolicyDetails(policyType.Home);
  const condo = home ? null : currentPolicyDetails(policyType.Condo);
  return {
    policyLimitBIPD: auto?.autoCoverage?.policyLimitBIPD,
    policyLimitUMBI: auto?.autoCoverage?.policyLimitUMBI,
    policyLimitUIMBI: auto?.autoCoverage?.policyLimitUIMBI,
    drivers: auto?.drivers,
    cars: auto?.cars,
    // Resolve by policy type: condo records can carry a stale homeCoverage scaffold whose
    // default coverageX would win a `??` chain (same hazard as #31428)
    coverageX: home ? home.homeCoverage?.coverageX : condo?.condoCoverage?.coverageX
  };
};

/**
 * Best-effort lookup of the policy bound from an offer (GitHub issue #31622).
 * Returns `{ id, accountId }` when the offer has already been purchased, or null when it
 * hasn't — and also null on any lookup failure, so the warning banner never blocks or
 * breaks the offer page.
 *
 * `includeLineage` (#35936): a bound policy records only the exact offer version it was bound
 * from, while every save mints a new version. Asking for the whole lineage makes earlier (and
 * later) versions of a bought quote show the banner too, instead of looking unpurchased.
 */
export const fetchBoundPolicy = async (client, offerId) => {
  try {
    const { data, error } = await client.query({
      query: GET_POLICY_FROM_OFFER_ID,
      variables: { offerId, includeLineage: true },
      fetchPolicy: 'no-cache'
    });
    if (error || !data?.getPolicyFromOfferId) {
      return null;
    }
    // offerId (#35936): the id of the offer this policy was actually bound from. When it equals the
    // offer being viewed, the match is EXACT (bound from this exact quote); otherwise it is a FUZZY
    // member+location match the caller should treat as a soft warning, not a hard block.
    const { id, accountId, offerId: boundFromOfferId } = data.getPolicyFromOfferId;
    return { id, accountId, offerId: boundFromOfferId };
  } catch {
    return null;
  }
};

/**
 * Guard for late-arriving bound-policy lookups (#31622): a result may only be applied if
 * it belongs to the most recently REQUESTED offer. Comparing against the loaded offer's id
 * instead would discard the legitimate result whenever the (fast, GSI-backed) lookup wins
 * the race against the heavy GET_OFFER query — i.e. nearly always, and always on first load.
 */
export const shouldApplyBoundPolicyResult = (forOfferId, latestRequestedOfferId) =>
  forOfferId === undefined || forOfferId === latestRequestedOfferId;

// HB/AB are bundle-discounted monoline options for offers linked to an existing customer:
// the shared link carries policyType=HA, but quote-fe only binds the H/A half (it honors
// noBindAuto/noBindHome for existing customers), so rejections gate on that half alone.
const optionToBoundPolicyType = {
  [policyType.HomeBundle]: policyType.Home,
  [policyType.AutoBundle]: policyType.Auto
};

/**
 * Whether the given option is affected by a soft rejection, scoped via
 * rejectionTypeToPolicyTypeMap so a rejection on the opposite policy type doesn't block a
 * clean monoline option (GitHub issue #31905). Rejection types without a map entry don't
 * block, matching the checkout rejectionsSchema and the CannotPurchase notification.
 */
export const hasSoftRejectionForOption = (rejections, selectedOption) => {
  const boundPolicyType = optionToBoundPolicyType[selectedOption] ?? selectedOption;
  return Boolean(
    rejections?.some(
      (rejection) =>
        rejection.level === 'soft' && rejectionTypeToPolicyTypeMap[rejection.type]?.includes(boundPolicyType)
    )
  );
};

/**
 * Whether a rejection HARD-blocks the given option from moving forward through the offer's
 * checkout / pre-bind validation (the `rejectionsSchema` gate).
 *
 * A rejection blocks when its type maps to the selected option — EXCEPT a soft rejection on a
 * farm-based option (F/Z/Y). Farm offers resolve soft rejections through the UW-review flow and gate
 * checkout separately on UW approval (the "Continue to checkout" button is disabled until approved),
 * so a soft farm rejection must NOT block here — otherwise the agent is trapped before they can even
 * reach the "Continue to Underwriting Review" form (GitHub issue #33355, e.g. the EQ Zone-2 approval
 * rejection 50133). Home/auto keep blocking on soft rejections: for them this predicate IS the
 * checkout gate, and UW approval clears the rejection rather than bypassing it.
 */
export const rejectionBlocksOptionCheckout = (rejection, selectedOption) => {
  if (!rejectionTypeToPolicyTypeMap[rejection.type]?.some((type) => type === selectedOption)) {
    return false;
  }
  if (isFarmBasedPolicyType(selectedOption) && rejection.level === 'soft') {
    return false;
  }
  return true;
};

// Newer-offer lookup for the auto-redirect: FE Meilisearch first, then the
// staff-appsync-be query for browsers that can't reach Meilisearch (partner firewalls on
// comparative-rater bridges). Never throws — returns null so the lookup can't block getOffer.
export const findNewerStaffEditedOffer = async (client, currentOffer, userPreferences) => {
  try {
    return await searchForNewerStaffEditedOffer(currentOffer, userPreferences);
  } catch (error) {
    console.warn('FE search for newer offer failed, falling back to backend query:', error);
  }
  try {
    const response = await client.query({
      query: GET_MOST_RECENT_STAFF_EDITED_OFFER,
      // Send the current offer's policy types so the backend applies the same product-set gate the
      // FE search does (#34790) — otherwise this fallback is a way around it.
      variables: {
        webUserId: currentOffer.webUserId,
        optionTypes: (currentOffer.options ?? []).map((option) => option?.type).filter(Boolean)
      },
      fetchPolicy: 'no-cache'
    });
    const mostRecent = response.data?.getMostRecentStaffEditedOffer;
    // The backend doesn't know which offer is being viewed — apply the strictly-newer gate here
    const isStrictlyNewer =
      mostRecent?.updatedDateTime &&
      (!currentOffer.updatedDateTime ||
        new Date(mostRecent.updatedDateTime).getTime() > new Date(currentOffer.updatedDateTime).getTime());
    if (mostRecent?.offerId && mostRecent.offerId !== currentOffer.id && isStrictlyNewer) {
      return {
        id: mostRecent.offerId,
        webUserId: currentOffer.webUserId,
        updatedDateTime: mostRecent.updatedDateTime
      };
    }
    return null;
  } catch (fallbackError) {
    console.warn('Backend fallback for newer offer failed:', fallbackError);
    return null;
  }
};

export const pollPolicyTable = async (client, offerId, { maxRetries }) => {
  let currentAttempt = 0;
  while (currentAttempt < maxRetries) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      currentAttempt += 1;
      const { data, error } = await client.query({
        query: GET_POLICY_FROM_OFFER_ID,
        variables: { offerId }
      });
      if (error) {
        throw error;
      }
      const { accountId } = data.getPolicyFromOfferId;
      return { data: { accountId } };
    } catch (error) {
      if (currentAttempt >= maxRetries) {
        throw error;
      }
    }
  }
};

/**
 * Which option the offer page should open on, in precedence order:
 *
 *  1. `checkoutOption` — the option the checkout route names in its own path
 *     (/offer/<offerId>/<option>/checkout).
 *  2. `raterOption` — `?type=` on the URL. Comparative-rater bridge links (#34438) arrive as
 *     /offer/<offerId>?rater=ez&type=HA, and that `type` is the option the agent actually quoted
 *     in EZLynx/PL Rater/TurboRater. It deliberately outranks the offer record: an RC1 request
 *     that carried only auto inputs is persisted with `quote.selectedOption: 'A'` even when Branch
 *     went on to rate an HA bundle off prefilled property data, so trusting the record drops the
 *     agent onto monoline auto with no sign the bundle they asked for was ever rated.
 *  3. the offer record's own `quote.selectedOption`.
 *  4. an existing-customer bundle (HB/AB) — a real selection that has no matching option entry,
 *     kept only while the HA option backing it survived (every consumer resolves HB/AB to the
 *     HA option, so a rejection that dropped HA makes HB/AB unusable).
 *  5. whatever we rated, HA/AR bundles first.
 *
 * The first three only win when they name an option actually on the offer, so a stale, misspelled
 * or rejected value falls through to the next source rather than selecting nothing.
 */
export const getInitialSelectedOption = (offer, { checkoutOption, raterOption } = {}) => {
  const quoteSelectedOption = offer.quote.selectedOption;
  if (!offer.options?.length) {
    return getQuoteFriendlySelectedOption(quoteSelectedOption);
  }
  const optionOnOffer = (type) => offer.options.find((option) => option.type === type)?.type;
  const isHomeOrAutoBundle = [policyType.HomeBundle, policyType.AutoBundle].includes(quoteSelectedOption);
  return (
    optionOnOffer(checkoutOption) ||
    optionOnOffer(raterOption) ||
    optionOnOffer(quoteSelectedOption) ||
    (offer.quote.existingCustomer &&
    isHomeOrAutoBundle &&
    optionOnOffer(getQuoteFriendlySelectedOption(quoteSelectedOption))
      ? quoteSelectedOption
      : null) ||
    offer.options
      .map((option) => option.type)
      .sort((a, b) => {
        if (a === policyType.HABundle) {
          return -1;
        }

        if (b === policyType.HABundle) {
          return 1;
        }

        if (a === policyType.ARBundle) {
          return -1;
        }

        if (b === policyType.ARBundle) {
          return 1;
        }

        return 0;
      })[0]
  );
};

/**
 * Where the rater interstitial sends the agent once getMergedOffer answers, and whether the bridge
 * link's query string follows.
 *
 * getMergedOffer returns a truthy offer on every path, including its many "returning original
 * offer" exits (not from RC1, no affinity, no eligible merge candidate, …), so the interstitial
 * cannot tell a merge from a no-op by truthiness alone — only by whether the id changed.
 *
 * A real merge lands on a NEW offer whose `quote.selectedOption` the backend has already set to the
 * bundle, so the rater's `type` must not follow it: a monoline `type` from the original link would
 * re-select the monoline option and quietly undo the merge (the offer provider re-runs getOffer off
 * the route-param change, and getOffer reads the option from the URL). A no-op returns the SAME
 * offer, where dropping the query string would throw away the rater's requested option from the URL
 * any later getOffer re-reads — the #34438 bug, one redirect further along.
 */
export const getMergedOfferRedirectPath = ({ offerId, mergedOfferId, search }) =>
  `/offer/${mergedOfferId}${mergedOfferId === offerId ? search || '' : ''}`;
